import React from 'react';

import GoogleMapReact from 'google-map-react';

import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import { useGoogleKeysProviderContext } from '@common/react/components/google/GoogleKeysProvider/GoogleKeysProvider';

interface MapProps {
	address: string;
	mapKey?: string;
	defaultProps?: {
		center: { lat: number, lng: number },
		zoom: number,
	};
	className?: string;
	onClick?: (e) => void;
	renderMarker?: (map, maps) => void;
	withoutIntersectionObserver?: boolean;
	onGoogleApiLoaded?: (map, maps) => void;
}

const codeAddress = (map, maps, address: string) => {
	const geocoder = new maps.Geocoder();
	let marker;
	geocoder.geocode({ address }, (results: Array<any>, status: string) => {
		if (status === 'OK') {
			map.setCenter(results[0].geometry.location);
			marker = new maps.Marker({
				map,
				position: results[0].geometry.location,
			});
		} else {
			console.log(`Geocode was not successful for the following reason: ${status}`);
		}
	});
	return marker;
};

const GoogleMap: React.FC<MapProps> = (props) => {
	const {
		defaultProps = {
			center: { lat: -34.397, lng: 150.644 },
			zoom: 12,
		},
		address,
		className = 'map',
		onClick,
		renderMarker = (map, maps) => codeAddress(map, maps, address),
		withoutIntersectionObserver,
		onGoogleApiLoaded,
		mapKey = useGoogleKeysProviderContext()?.state?.mapKey ?? '',
	} = props;

	const ref = React.useRef(null);
	const [visible, setVisible] = React.useState(!!withoutIntersectionObserver);
	const [map, setMap] = React.useState();
	const [maps, setMaps] = React.useState();

	React.useEffect(() => {
		if (ref.current && !withoutIntersectionObserver) {
			const intersectionObserver = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting) {
					setVisible(true);
				}
			}, {
				rootMargin: '15px',
				threshold: 0.01,
			});

			intersectionObserver.observe(ref.current);
			return () => intersectionObserver.disconnect();
		}
	}, []);

	React.useEffect(() => {
		if (map && maps) {
			renderMarker(map, maps);
		}
	}, [address]);

	return (
		<div className={className} ref={ref}>
			{visible ? <GoogleMapReact
				onClick={onClick}
				bootstrapURLKeys={{ key: mapKey }}
				defaultCenter={defaultProps.center}
				defaultZoom={defaultProps.zoom}
				onGoogleApiLoaded={({ map, maps }) => {
					setMap(map);
					setMaps(maps);
					renderMarker(map, maps);
					onGoogleApiLoaded?.(map, maps);
				}}
			/> : <Loader />}
		</div>
	);
};

export default GoogleMap;
